import React from 'react';
import { ArrowUpRight, Mail, Phone, Linkedin, ExternalLink, Globe } from 'lucide-react';
import { CONTACT_EMAIL, CONTACT_PHONE } from '../constants';

const KEYWORDS = [
  "Security", "Engagement", "Communication", "Verifiability", "Leadership", 
  "2-Way Communication", "Voting", "Polling", "Surveying", "Smart City", 
  "Infrastructure", "Intelligence", "Sovereignty", "Participation", "Transparency",
  "Boulai", "PolicyNow", "Civic Tech", "Predictive Planning"
];

const Footer: React.FC = () => {
  return (
    <footer id="contact" className="pt-32 pb-12 px-6 bg-brand-black border-t border-white/5 relative overflow-hidden">
      
      {/* SCROLLING MARQUEE */}
      <div className="absolute top-0 left-0 w-full overflow-hidden border-b border-white/5 bg-white/[0.02] py-4 pointer-events-none">
        <div className="flex whitespace-nowrap animate-marquee">
          <div className="flex shrink-0 items-center">
            {KEYWORDS.map((word, i) => (
              <React.Fragment key={i}>
                <span className="text-[10px] uppercase tracking-[0.5em] font-black text-white/20 mx-10">{word}</span>
                <span className="w-1 h-1 rounded-full bg-brand-blue/30 mx-2"></span>
              </React.Fragment>
            ))}
          </div>
          <div className="flex shrink-0 items-center">
            {KEYWORDS.map((word, i) => (
              <React.Fragment key={`repeat-${i}`}>
                <span className="text-[10px] uppercase tracking-[0.5em] font-black text-white/20 mx-10">{word}</span>
                <span className="w-1 h-1 rounded-full bg-brand-blue/30 mx-2"></span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-16">
        <div className="grid md:grid-cols-2 gap-20 mb-32">
          <div className="space-y-12">
             <h2 className="text-5xl md:text-8xl font-serif italic mb-8 tracking-tighter uppercase leading-[0.85] text-white">
              Start <br /> Leading<span className="text-brand-blue not-italic animate-pulse">.</span>
            </h2>
            <p className="text-white/40 max-w-sm font-medium leading-relaxed">
              Every government, organization, and community is different. Request a tailored demo or schedule a quick walkthrough of the neural engagement stack.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <a 
                href="https://www.linkedin.com/company/boulai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-4 glass rounded-xl hover:bg-brand-blue hover:text-white transition-all transform hover:scale-110 active:scale-95 flex items-center justify-center shadow-lg hover:shadow-brand-blue/20"
              >
                <Linkedin size={20} />
              </a>
              <a 
                href="https://www.f6s.com/company/boulai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-6 py-4 glass rounded-xl text-[10px] uppercase tracking-widest font-black hover:bg-white/5 transition-all flex items-center space-x-2"
              >
                <span>F6S Profile</span>
                <ExternalLink size={14} />
              </a>
              <a 
                href="https://www.venturelab.ca/portfolio/boulai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-6 py-4 glass rounded-xl text-[10px] uppercase tracking-widest font-black hover:bg-white/5 transition-all flex items-center space-x-2"
              >
                <span>VentureLab</span>
                <ExternalLink size={14} />
              </a>
            </div>
          </div>

          <div className="space-y-16">
            <div className="group cursor-default">
              <span className="text-xs uppercase tracking-[0.3em] text-white/20 font-bold block mb-10">Contact & Demo</span>
              <div className="space-y-10">
                 <a 
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="flex items-center space-x-6 text-2xl md:text-4xl font-serif hover:text-brand-blue transition-all group"
                 >
                    <div className="w-12 h-12 bg-brand-blue/10 rounded-xl flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-all shadow-[0_0_15px_rgba(86,190,249,0.1)]">
                      <Mail size={24} />
                    </div>
                    <span className="border-b border-transparent group-hover:border-brand-blue/30 transition-all">{CONTACT_EMAIL}</span>
                    <ArrowUpRight size={24} className="opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0" />
                 </a>
                 <a 
                    href={`tel:${CONTACT_PHONE.replace(/-/g, '')}`}
                    className="flex items-center space-x-6 text-2xl md:text-4xl font-serif hover:text-brand-blue transition-all group"
                 >
                    <div className="w-12 h-12 bg-brand-blue/10 rounded-xl flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-all shadow-[0_0_15px_rgba(86,190,249,0.1)]">
                      <Phone size={24} />
                    </div>
                    <span className="border-b border-transparent group-hover:border-brand-blue/30 transition-all">{CONTACT_PHONE}</span>
                 </a>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center pt-12 border-t border-white/5 text-[10px] uppercase tracking-[0.2em] text-white/20 font-bold">
          <p>© 2024 BOULAI PLATFORM. DATA SOVEREIGNTY BY DESIGN.</p>
          <div className="flex items-center space-x-12 mt-6 md:mt-0">
            <div className="flex items-center space-x-2 text-brand-blue opacity-50">
               <div className="w-1.5 h-1.5 rounded-full bg-brand-blue animate-pulse"></div>
               <span>Verified Identity Node</span>
            </div>
            <div className="flex items-center space-x-2 text-brand-blue opacity-50">
               <Globe size={12} />
               <span>Sovereign Storage</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;